import React, { useState, useCallback } from 'react'
import { GridCell } from './GridCell'
import { LineItemCell } from './LineItemCell'
import { Spinner } from 'components/ui/spinner'
import type { FinancialsGridProps, CellPosition, EditingCell } from '../types/financials.types'

export const FinancialsGrid: React.FC<FinancialsGridProps> = ({
	incomeStatement,
	onCellChange,
	onLineItemReorder,
	onLineItemDelete,
	onQuarterDelete,
	isLoading = false,
	isReadOnly = false,
	presenceUsers = [],
}) => {
	const [editingCell, setEditingCell] = useState<EditingCell | null>(null)
	const [hoveredQuarterId, setHoveredQuarterId] = useState<string | null>(null)

	const handleCellEdit = useCallback((lineItemId: string, quarterId: string, value: number) => {
		setEditingCell({
			position: { lineItemId, quarterId },
			originalValue: value,
			currentValue: value.toString(),
		})
	}, [])

	const handleCellChange = useCallback(
		(value: number) => {
			if (editingCell) {
				onCellChange(editingCell.position.lineItemId, editingCell.position.quarterId, value)
			}
		},
		[editingCell, onCellChange],
	)

	const handleCellBlur = useCallback(() => {
		setEditingCell(null)
	}, [])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (!editingCell) return

			// Navigate with arrow keys
			if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
				// TODO: Implement cell navigation logic
			}
		},
		[editingCell],
	)

	const getValue = (lineItemId: string, quarterId: string): number => {
		const lineItem = incomeStatement.lineItems.find(li => li.id === lineItemId)
		if (!lineItem) return 0

		const valueObj = lineItem.values.find(v => v.quarter === quarterId)
		return valueObj?.value || 0
	}

	if (isLoading) {
		return <Spinner containerHeight="50vh" />
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse" role="grid">
				<thead>
					<tr className="bg-gray-100">
						<th
							className="px-3 py-2 text-left font-semibold text-gray-700 border-r border-b border-gray-300 sticky left-0 bg-gray-100 z-10"
							style={{ minWidth: '200px' }}
						>
							Line Item
						</th>
						{[...incomeStatement.quarters]
							.sort((a, b) => b.order - a.order) // Sort descending (most recent first)
							.map(quarter => (
								<th
									key={quarter.id}
									className="px-3 py-2 text-right font-semibold text-gray-700 border-r border-b border-gray-300 relative group"
									style={{ minWidth: '100px' }}
									onMouseEnter={() => setHoveredQuarterId(quarter.id)}
									onMouseLeave={() => setHoveredQuarterId(null)}
								>
									<div className="flex items-center justify-end">
										{/* Delete button - appears on hover */}
										{!isReadOnly && hoveredQuarterId === quarter.id && onQuarterDelete && (
											<button
												onClick={e => {
													e.stopPropagation()
													onQuarterDelete(quarter.id)
												}}
												className="mr-2 p-1 rounded hover:bg-red-100 transition-colors"
												title="Delete quarter"
											>
												<svg
													className="w-4 h-4 text-red-600"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
													/>
												</svg>
											</button>
										)}
										<span>{quarter.label}</span>
									</div>
								</th>
							))}
					</tr>
				</thead>
				<tbody>
					{[...incomeStatement.lineItems]
						.sort((a, b) => a.order - b.order)
						.map(lineItem => (
							<tr key={lineItem.id} className="hover:bg-gray-50">
								<LineItemCell
									lineItem={lineItem}
									isDefault={lineItem.isDefault}
									isDraggable={!isReadOnly}
									onDelete={lineItem.isDefault ? undefined : () => onLineItemDelete(lineItem.id)}
								/>
								{[...incomeStatement.quarters]
									.sort((a, b) => b.order - a.order) // Sort descending (most recent first)
									.map(quarter => {
										const value = getValue(lineItem.id, quarter.id)
										const isEditing =
											editingCell?.position.lineItemId === lineItem.id &&
											editingCell?.position.quarterId === quarter.id

										return (
											<GridCell
												key={`${lineItem.id}-${quarter.id}`}
												lineItem={lineItem}
												quarter={quarter}
												value={value}
												isCalculated={lineItem.type === 'calculated'}
												isEditing={isEditing}
												isReadOnly={isReadOnly}
												onEdit={() => handleCellEdit(lineItem.id, quarter.id, value)}
												onChange={handleCellChange}
												onBlur={handleCellBlur}
												onKeyDown={handleKeyDown}
											/>
										)
									})}
							</tr>
						))}
				</tbody>
			</table>

			{/* Presence Indicators */}
			{presenceUsers.length > 0 && (
				<div className="mt-4 flex items-center space-x-2 text-sm text-gray-600">
					<span className="flex items-center">
						<svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
							<path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
						</svg>
						Currently viewing:
					</span>
					{presenceUsers.map((user, index) => (
						<span key={user.userId}>
							{user.userName}
							{index < presenceUsers.length - 1 && ','}
						</span>
					))}
				</div>
			)}
		</div>
	)
}
